import { lazy } from "react";

import { FEATURES } from "types/landlord/partner";
import {
  withCrmAccessGate,
  withCrmFeatureGate
} from "shared/app/BoomCrmPaywall/withCrmFeatureGate";
import {
  useIsListingSettingsGated,
  useIsPhoneNumbersGated,
  useIsWorkflowsGated
} from "shared/app/BoomCrmPaywall/hooks";
import { NotFoundView } from "views/not-found";
import { VerificationTemplatesView } from "views/verification-templates";
import { VerificationTemplatesIdView } from "views/verification-templates-id";
import { ApplicationsIdOverview } from "views/applications-id";
import { AddPropertyPage } from "views/properties-add";
import { PropertyDetail } from "views/properties-id";
import { Properties } from "views/properties";
import { Units } from "views/units";
import { UnitDetail } from "views/properties-id-units-id";
import { AddUnitsView } from "views/properties-id-units-add";
import { RentReportingView } from "views/rent-reporting";
import { RentReportingUpload } from "views/rent-reporting-upload";
import { AddNewSubscriber } from "views/rent-reporting-add";
import { SubscriptionDetails } from "views/rent-reporting-id";
import { EditSubscriptionDetails } from "views/rent-reporting-id-edit";
import { RentersSettingsView } from "views/rent-reporting-settings";
import { RentReportingUploadIdView } from "views/rent-reporting-upload-id";
import { RentReportingCsvAnalysisView } from "views/rent-reporting-upload-csv-analysis";
import { ResubscribeSubscriptionDetails } from "views/rent-reporting-id-resubscribe";
import { IdentityTheftProtectionView } from "views/identity-theft-protection";
import { PropertyGroups } from "views/property-groups";
import { PropertyGroupsConfiguration } from "views/property-groups-configuration";
import { PropertyGroupDetail } from "views/property-groups-id";
import { ApplicationsIdReportsPrintReceipt } from "views/applications-id-reports-print-receipt";
import { BoomNotificationView } from "views/boom-notifications";
import { DiscountsRouteWrapper } from "providers/Routes/components/DiscountsRouteWrapper";
import { LandlordSurveysView } from "views/landlord-surveys";
import { LandlordSurveysIdView } from "views/landlord-surveys-id";
import { ApplicationsView } from "views/applications";
import { ApplicationReportsRedirect } from "providers/Routes/redirects";
import type { PartnerUserPermissions } from "providers/Auth";

import type { RouteConfig } from "../types";

const RentersPage = lazy(() => import("views/renters"));
const InboxView = lazy(() => import("views/inbox"));
const CreditImpactView = lazy(() => import("views/credit-impact"));
const AnalyticsView = lazy(() => import("views/analytics"));
const TaskView = lazy(() => import("views/tasks"));
// Lead detail is also reached from Contacts, Applications and the Inbox, so it
// paywalls on the same entitlement as the Leads list instead of leaking the
// lead page to a partner without tenant_leads (CRM-759).
const LeadsIdView = withCrmFeatureGate(
  FEATURES.TENANT_LEADS,
  "leads",
  lazy(() => import("views/leads-id"))
);
const LeadsView = lazy(() => import("views/leads"));
const OutcomesView = lazy(() => import("views/outcomes"));
const ContactsView = lazy(() => import("views/contacts"));
const ContactsIdView = lazy(() => import("views/contacts-id"));
const ForbiddenView = lazy(() => import("views/forbidden"));
// Workflows are granted by Core; the routes stay open to the whole CRM
// ecosystem and paywall themselves once the entitlement is revoked (CRM-759).
const WorkflowsView = withCrmAccessGate(
  useIsWorkflowsGated,
  "workflows",
  lazy(() => import("views/workflows"))
);
const WorkflowDetail = withCrmAccessGate(
  useIsWorkflowsGated,
  "workflows",
  lazy(() => import("views/workflows-id"))
);
// Routable for the whole CRM ecosystem; paywalled without the `communication_campaigns` entitlement.
const CommunicationCampaignsView = withCrmFeatureGate(
  FEATURES.COMMUNICATION_CAMPAIGNS,
  "communicationCampaigns",
  lazy(() => import("views/campaigns"))
);
const CommunicationCampaignDetail = withCrmFeatureGate(
  FEATURES.COMMUNICATION_CAMPAIGNS,
  "communicationCampaigns",
  lazy(() => import("views/campaigns-id"))
);
// One builder serves both create and edit; it reads `:campaignId` when present.
const CommunicationCampaignBuilder = withCrmFeatureGate(
  FEATURES.COMMUNICATION_CAMPAIGNS,
  "communicationCampaigns",
  lazy(() => import("views/campaigns-builder"))
);
// Listing settings ride the showings entitlement (CRM-908); granted by Core, so
// the paywall only appears once an admin revokes it — see useIsListingSettingsGated.
const ListingSettingsView = withCrmAccessGate(
  useIsListingSettingsGated,
  "listingSettings",
  lazy(() => import("views/listing-settings"))
);
const ListingTemplatesIdView = withCrmAccessGate(
  useIsListingSettingsGated,
  "listingSettings",
  lazy(() => import("views/listing-settings-id"))
);
// Core-plan surfaces: routable for the whole CRM ecosystem, paywalled when the
// entitlement is missing (CRM-759).
const ShowingsView = withCrmFeatureGate(
  FEATURES.SHOWINGS,
  "showings",
  lazy(() => import("views/showings"))
);
const LockboxesView = withCrmFeatureGate(
  FEATURES.LOCKBOXES,
  "lockboxes",
  lazy(() => import("views/lockboxes"))
);
const PhoneNumbersView = withCrmAccessGate(
  useIsPhoneNumbersGated,
  "phoneNumbers",
  lazy(() => import("views/phone-numbers"))
);
const MarketRentView = lazy(() => import("views/market-rent"));
const MarketRentReportView = lazy(() => import("views/market-rent-id"));
const CallsView = lazy(() => import("views/calls"));
const CollectionsOutstandingBalanceView = lazy(
  () => import("views/collections-outstanding-balance")
);

type PartnerType = {
  isBoomScreen: boolean;
  isBoomCRM: boolean;
  isNativeChatActive: boolean;
  isCommunicationSms: boolean;
  isBoomScreenOutcomes: boolean;
  isProvisionedPhoneNumber: boolean;
  // Optional because `getActiveRoutes` (active/routes.ts) declares its own
  // narrower PartnerType and forwards it here; requiring the field there would
  // break assignability. The real auth context always supplies it.
  isCommunicationPhone?: boolean;
  isBoomOps: boolean;
  isTasks: boolean;
  isLockboxes: boolean;
  isShowings: boolean;
  isMarketRent: boolean;
  isCrmListingSync: boolean;
  isCollectionsOutstandingBalance: boolean;
  adminPrivileges: boolean;
};

export const getMainRoutes = (
  type: PartnerType,
  permissions: PartnerUserPermissions
): RouteConfig[] => [
  // BoomCRM — native chat lives in the Inbox and ships to BoomScreen-only
  // partners too, for as long as chat stays switched on (see
  // `isNativeChatActive`). Boom ops also keep Inbox access to verify migrated
  // chat. The free Core plan grants communication_sms, so free partners reach
  // the SMS-only Inbox through the isBoomCRM arm (CRM-759).
  {
    path: "inbox",
    component: InboxView,
    when:
      type.isBoomCRM ||
      (type.isNativeChatActive && type.isBoomScreen) ||
      type.isBoomOps
  },
  // Renter leads and Contacts are granted by the free Core plan; owner leads
  // stay Pro but are not a separate surface yet.
  { path: "leads", component: LeadsView, when: type.isBoomCRM },
  { path: "leads/:leadId", component: LeadsIdView, when: type.isBoomCRM },
  { path: "contacts", component: ContactsView, when: type.isBoomCRM },
  {
    path: "contacts/:contactId",
    component: ContactsIdView,
    when: type.isBoomCRM
  },
  // Tasks is Pro-only, but the route stays reachable for the whole CRM
  // ecosystem so a free partner lands on the upgrade prompt (CRM-759).
  { path: "tasks", component: TaskView, when: type.isBoomCRM },
  {
    path: "listing-settings",
    component: ListingSettingsView,
    when: type.isBoomCRM
  },
  {
    path: "listing-settings/:id",
    component: ListingTemplatesIdView,
    when: type.isBoomCRM
  },
  // Lockboxes, Showings and Phone numbers are all granted by the free Core plan,
  // so these routes stay open to the whole CRM ecosystem and each view paywalls
  // itself. Dropping the route on a revoked entitlement made the path fall
  // through to NotFoundView, so anyone arriving from a bookmark, an email link
  // or a lead-detail deep link got a bare "Not found" with nothing to click
  // instead of the upgrade prompt (CRM-759).
  {
    path: "lockboxes",
    component: LockboxesView,
    when: type.isBoomCRM
  },
  {
    path: "showings",
    component: ShowingsView,
    when: type.isBoomCRM
  },
  {
    path: "market-rent",
    component: MarketRentView,
    when: type.isBoomCRM && type.isMarketRent
  },
  {
    path: "market-rent/:reportId",
    component: MarketRentReportView,
    when: type.isBoomCRM && type.isMarketRent
  },
  {
    path: "phone-numbers",
    component: PhoneNumbersView,
    // Collections partners need a number to send from, and may hold the
    // balances page without the CRM plan that normally carries this (BF-552).
    when: type.isBoomCRM || type.isCollectionsOutstandingBalance
  },
  // The call log is visibility, not calling: it is gated on the partner
  // feature only, so inbound-only users (no can_make_voice_calls) still see
  // who tried to reach them. Boolean() because the flag is optional above and
  // `when: undefined` would render the route.
  {
    path: "calls",
    component: CallsView,
    when: Boolean(type.isCommunicationPhone) && type.isProvisionedPhoneNumber
  },

  // Collections — phase 1 is the read-only outstanding balance list (BF-454).
  {
    path: "collections/outstanding-balance",
    component: CollectionsOutstandingBalanceView,
    when: type.isCollectionsOutstandingBalance
  },

  // Renters & Rent Reporting
  { path: "renters", component: RentersPage },
  { path: "rent-reporting/settings", component: RentersSettingsView },
  { path: "credit-impact", component: CreditImpactView },
  { path: "rent-reporting", component: RentReportingView },
  {
    path: "identity-theft-protection",
    component: IdentityTheftProtectionView
  },
  { path: "rent-reporting/:renterId", component: SubscriptionDetails },
  { path: "rent-reporting/upload", component: RentReportingUpload },
  {
    path: "rent-reporting/upload/csv-analysis",
    component: RentReportingCsvAnalysisView
  },
  { path: "rent-reporting/upload/:id", component: RentReportingUploadIdView },
  { path: "rent-reporting/add", component: AddNewSubscriber },
  {
    path: "rent-reporting/:renterId/edit",
    component: EditSubscriptionDetails
  },
  {
    path: "rent-reporting/:renterId/resubscribe",
    component: ResubscribeSubscriptionDetails
  },
  { path: "notifications", component: BoomNotificationView },

  // Properties
  { path: "properties", component: Properties },
  { path: "properties/add", component: AddPropertyPage },
  { path: "properties/:propertyId", component: PropertyDetail },
  { path: "properties/:propertyId/units/add", component: AddUnitsView },
  { path: "properties/:propertyId/units/:unitId", component: UnitDetail },
  { path: "units", component: Units },
  { path: "property-groups", component: PropertyGroups },
  { path: "property-groups/:id", component: PropertyGroupDetail },
  {
    path: "property-groups/configuration",
    component: PropertyGroupsConfiguration
  },

  // Applications
  { path: "applications", component: ApplicationsView },
  {
    path: "outcomes",
    component: OutcomesView,
    when: type.isBoomScreen && type.isBoomScreenOutcomes && type.adminPrivileges
  },
  { path: "applications/:applicationId", component: ApplicationsIdOverview },
  {
    path: "applications/:applicationId/reports",
    component: ApplicationReportsRedirect
  },
  {
    path: "applications/:applicationId/reports/print/receipt",
    component: ApplicationsIdReportsPrintReceipt
  },

  // Workflows
  { path: "workflows", component: WorkflowsView },
  { path: "workflows/:workflowId", component: WorkflowDetail },

  // Campaigns
  {
    path: "campaigns",
    component: CommunicationCampaignsView,
    when: type.isBoomCRM
  },
  {
    path: "campaigns/new",
    component: CommunicationCampaignBuilder,
    when: type.isBoomCRM
  },
  {
    path: "campaigns/:campaignId",
    component: CommunicationCampaignDetail,
    when: type.isBoomCRM
  },
  {
    path: "campaigns/:campaignId/edit",
    component: CommunicationCampaignBuilder,
    when: type.isBoomCRM
  },

  // Verification & Discounts
  {
    path: "verification-templates",
    component: VerificationTemplatesView,
    when: permissions.canAccessScreeningTemplates
  },
  {
    path: "verification-templates/:id",
    component: VerificationTemplatesIdView,
    when: permissions.canAccessScreeningTemplates
  },
  {
    path: "discounts",
    component: DiscountsRouteWrapper,
    when: permissions.canManageDiscounts
  },

  // Surveys
  { path: "landlord-surveys", component: LandlordSurveysView },
  { path: "landlord-surveys/:id", component: LandlordSurveysIdView },

  // Analytics — always available; feature flags gate the subtabs inside the view
  { path: "analytics", component: AnalyticsView },

  // Error pages
  { path: "forbidden", component: ForbiddenView },
  { path: "*", component: NotFoundView }
];
